/**
 * Site-metadata helper functions used by `useFetchConfig`.
 *
 * Originally inlined in `controllers/config/index.tsx`. Extracted into this
 * sibling module so they can be unit-tested directly and so they can be
 * referenced by upcoming refactors (e.g. delegation to a shared canonical
 * implementation in `@atlassian/rovo-connectors-config`).
 *
 * **Not exported from the package.** Lives at
 * `controllers/config/site-metadata-helpers.ts` and is intentionally absent
 * from `package.json` `"exports"`. External consumers should keep going
 * through the `./use-fetch-config` subpath.
 */

import { type TSearchConfigAPIResponse } from '@atlassian/search-client/fetch-search-config';

/** Matches `ari:cloud:<product>::<resource-type>/<cloudId>` and captures the cloudId. */
const EXTRACT_CLOUD_ID = /ari:cloud:.*?\/(.*)/;

export const getFirstCloudId = (firstPartyResponse: TSearchConfigAPIResponse): string => {
	const match = firstPartyResponse.resources[0]?.id.match(EXTRACT_CLOUD_ID);
	const extractedCloudId = match ? match[1] : '';

	return extractedCloudId;
};

export const isRovoEnabled = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): boolean | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isRovoEnabled;
};

export const getJiraIssueIndexedForTenant = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): boolean | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isJiraIssueIndexed;
};

export const getProductEdition = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['productEdition'] | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productEdition,
	)?.productEdition;
};

export const getProductDataRegions = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['productDataRegions'] | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productDataRegions,
	)?.productDataRegions;
};

export const getRovoFeatures = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['rovoFeatures'] | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.rovoFeatures,
	)?.rovoFeatures;
};
